import type { ChatRenderContext } from "@/components/shared/chat-message-types";
import { useHubStore } from "@/components/hub/use-hub-store";
import { useShareableEntity } from "@/lib/api/shareable-entities";
import { findParticipantById } from "@/lib/hooks/conversations/use-conversation-participants";
import { parseMessageData } from "@/lib/utils/parse-message-data";
import { useCallback, useMemo } from "react";

export interface EntitySharedData {
  entity_type: "note" | "moment" | "swap";
  entity_id: string;
  owner_id: string;
  preview_url?: string | null;
  second_preview_url?: string | null;
  swap_action?: "started" | "pending_mentor" | "pending_teen" | "completed";
}

export function useEntityShared(
  message: { data?: string },
  options?: { context?: ChatRenderContext },
) {
  const entityData = useMemo(
    () => parseMessageData<EntitySharedData>(message.data),
    [message.data],
  );

  const open = useHubStore((state) => state.open);
  const navigateToView = useHubStore((state) => state.navigateToView);

  const { isError: isUnavailable } = useShareableEntity(
    entityData?.entity_id ?? "",
  );

  const ownerName = useMemo(() => {
    return (
      findParticipantById(options?.context, entityData?.owner_id).firstName ??
      "Someone"
    );
  }, [entityData?.owner_id, options?.context]);

  const openInHub = useCallback(() => {
    if (!entityData || isUnavailable) return;
    open();
    const viewMap = {
      moment: "view-moment",
      note: "view-note",
      swap: "view-swap",
    } as const;
    navigateToView(viewMap[entityData.entity_type], entityData.entity_id);
  }, [entityData, isUnavailable, open, navigateToView]);

  return { entityData, isUnavailable, ownerName, openInHub };
}
